import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Trash2, Plus, X, Pencil, Save, GripVertical } from "lucide-react";

export const Route = createFileRoute("/admin/products")({
  component: AdminProducts,
});

type Product = {
  id: string;
  slug: string;
  name: string;
  description: string;
  price_cents: number;
  compare_at_price_cents: number | null;
  image_url: string;
  images: string[];
  category: string;
  stock: number;
  featured: boolean;
  rating: number;
  reviews_count: number;
};

const MAX_IMAGES = 5;

const empty: Omit<Product, "id"> = {
  slug: "",
  name: "",
  description: "",
  price_cents: 0,
  compare_at_price_cents: null,
  image_url: "",
  images: [],
  category: "all",
  stock: 0,
  featured: false,
  rating: 4.3,
  reviews_count: 0,
};

function AdminProducts() {
  const [items, setItems] = useState<Product[]>([]);
  const [form, setForm] = useState<Omit<Product, "id">>(empty);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState<{ slug: string; label: string }[]>([]);

  const [rowDragIdx, setRowDragIdx] = useState<number | null>(null);
  const [rowOverIdx, setRowOverIdx] = useState<number | null>(null);

  const load = async () => {
    const { data, error } = await supabase
      .from("products")
      .select("*")
      .order("sort_order", { ascending: true })
      .order("created_at", { ascending: false });
    if (error) toast.error(error.message);
    else setItems((data as Product[]) ?? []);
  };

  const reorderRows = async (from: number, to: number) => {
    if (from === to) return;
    const next = [...items];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    setItems(next);
    const updates = await Promise.all(
      next.map((p, i) =>
        supabase.from("products").update({ sort_order: i }).eq("id", p.id)
      )
    );
    const failed = updates.find((u) => u.error);
    if (failed?.error) toast.error(failed.error.message);
    else toast.success("Order saved");
  };


  const loadCategories = async () => {
    const { data } = await supabase
      .from("categories")
      .select("slug,label")
      .eq("enabled", true)
      .order("position");
    setCategories(data ?? []);
  };

  useEffect(() => { load(); loadCategories(); }, []);

  const resetForm = () => { setForm(empty); setEditingId(null); };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    const payload = { ...form, images: form.images ?? [] };
    const { error } = editingId
      ? await supabase.from("products").update(payload).eq("id", editingId)
      : await supabase.from("products").insert(payload);
    setLoading(false);
    if (error) return toast.error(error.message);
    toast.success(editingId ? "Product updated" : "Product added");
    resetForm();
    load();
  };

  const startEdit = (p: Product) => {
    setEditingId(p.id);
    setForm({
      slug: p.slug,
      name: p.name,
      description: p.description ?? "",
      price_cents: p.price_cents,
      compare_at_price_cents: p.compare_at_price_cents ?? null,
      image_url: p.image_url ?? "",
      images: p.images ?? [],
      category: p.category ?? "all",
      stock: p.stock ?? 0,
      featured: !!p.featured,
      rating: p.rating ?? 4.3,
      reviews_count: p.reviews_count ?? 0,
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const remove = async (id: string) => {
    if (!confirm("Delete this product?")) return;
    const { error } = await supabase.from("products").delete().eq("id", id);
    if (error) return toast.error(error.message);
    toast.success("Deleted");
    if (editingId === id) resetForm();
    load();
  };

  const uploadFiles = async (files: FileList) => {
    const all = [form.image_url, ...form.images].filter(Boolean);
    const slots = MAX_IMAGES - all.length;
    if (slots <= 0) return toast.error(`Max ${MAX_IMAGES} images`);
    const list = Array.from(files).slice(0, slots);
    setLoading(true);
    const uploaded: string[] = [];
    for (const file of list) {
      const ext = file.name.split(".").pop() || "jpg";
      const path = `${Date.now()}-${Math.random().toString(36).slice(2)}.${ext}`;
      const { error } = await supabase.storage.from("product-images").upload(path, file, { upsert: false, contentType: file.type, cacheControl: "31536000" });
      if (error) { toast.error(error.message); continue; }
      const { data } = supabase.storage.from("product-images").getPublicUrl(path);
      uploaded.push(data.publicUrl);
    }
    setLoading(false);
    if (uploaded.length === 0) return;
    setForm((f) => {
      const next = { ...f };
      if (!next.image_url) {
        next.image_url = uploaded.shift()!;
      }
      next.images = [...next.images, ...uploaded].slice(0, MAX_IMAGES - 1);
      return next;
    });
    toast.success(`${uploaded.length + (form.image_url ? 0 : 1)} image(s) uploaded`);
  };

  const removeImage = (url: string) => {
    setForm((f) => {
      if (f.image_url === url) {
        const [first, ...rest] = f.images;
        return { ...f, image_url: first ?? "", images: rest };
      }
      return { ...f, images: f.images.filter((u) => u !== url) };
    });
  };

  const reorderImages = (from: number, to: number) => {
    if (from === to) return;
    setForm((f) => {
      const arr = [f.image_url, ...f.images].filter(Boolean);
      const [moved] = arr.splice(from, 1);
      arr.splice(to, 0, moved);
      const [first, ...rest] = arr;
      return { ...f, image_url: first ?? "", images: rest };
    });
  };

  const [dragIdx, setDragIdx] = useState<number | null>(null);
  const [dropActive, setDropActive] = useState(false);

  const onDropFiles = (e: React.DragEvent) => {
    e.preventDefault();
    setDropActive(false);
    const files = e.dataTransfer.files;
    if (files && files.length) uploadFiles(files);
  };

  const allImages = [form.image_url, ...form.images].filter(Boolean);

  return (
    <div className="space-y-8">
      <form onSubmit={submit} className="rounded-lg border border-border p-5 space-y-4 bg-card">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-medium">{editingId ? "Edit product" : "Add product"}</h2>
          {editingId && (
            <button type="button" onClick={resetForm} className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1">
              <X className="h-3 w-3" /> Cancel edit
            </button>
          )}
        </div>

        {/* Images first */}
        <div className="space-y-2">
          <label className="text-sm font-medium">Images ({allImages.length}/{MAX_IMAGES})</label>
          <div
            onDragOver={(e) => { e.preventDefault(); if (dragIdx === null) setDropActive(true); }}
            onDragLeave={() => setDropActive(false)}
            onDrop={onDropFiles}
            className={`flex flex-wrap gap-2 rounded-lg border-2 border-dashed p-3 transition-colors ${dropActive ? "border-foreground bg-muted/50" : "border-border"}`}
          >
            {allImages.map((url, i) => (
              <div
                key={url}
                draggable
                onDragStart={(e) => { setDragIdx(i); e.dataTransfer.effectAllowed = "move"; }}
                onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; }}
                onDrop={(e) => { e.preventDefault(); e.stopPropagation(); if (dragIdx !== null) reorderImages(dragIdx, i); setDragIdx(null); }}
                onDragEnd={() => setDragIdx(null)}
                className={`relative h-20 w-20 rounded overflow-hidden border border-border bg-muted cursor-move ${dragIdx === i ? "opacity-50" : ""}`}
                title="Drag to reorder"
              >
                <img src={url} alt="" className="h-full w-full object-cover pointer-events-none" />
                {i === 0 && <span className="absolute bottom-0 left-0 right-0 bg-foreground/80 text-background text-[10px] text-center py-0.5">Main</span>}
                <button type="button" onClick={() => removeImage(url)} className="absolute top-0.5 right-0.5 bg-destructive text-destructive-foreground rounded-full p-0.5">
                  <X className="h-3 w-3" />
                </button>
              </div>
            ))}
            {allImages.length < MAX_IMAGES && (
              <label className="h-20 w-20 flex flex-col items-center justify-center rounded border border-dashed border-border bg-background cursor-pointer hover:bg-muted text-[11px] text-muted-foreground text-center px-1">
                <Plus className="h-4 w-4 mb-0.5" />
                Add / Drop
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  className="hidden"
                  onChange={(e) => { const fs = e.target.files; if (fs && fs.length) uploadFiles(fs); e.target.value = ""; }}
                />
              </label>
            )}
          </div>
          <p className="text-[11px] text-muted-foreground">Drag files in to upload. Drag tiles to reorder — first image is the main one. Up to {MAX_IMAGES} total.</p>
        </div>


        {/* Price & stock */}

        <div className="grid sm:grid-cols-3 gap-3">
          <div className="space-y-1">
            <label className="text-sm font-medium">Price (₹)</label>
            <input
              required
              type="number"
              min="0"
              step="1"
              placeholder="Selling price"
              value={form.price_cents ? form.price_cents / 100 : ""}
              onChange={(e) => setForm({ ...form, price_cents: Math.round(Number(e.target.value) * 100) })}
              className="w-full border border-border px-3 py-2 rounded text-sm bg-background"
            />
          </div>
          <div className="space-y-1">
            <label className="text-sm font-medium">MRP / Real price (₹)</label>
            <input
              type="number"
              min="0"
              step="1"
              placeholder="Optional original price"
              value={form.compare_at_price_cents ? form.compare_at_price_cents / 100 : ""}
              onChange={(e) => setForm({ ...form, compare_at_price_cents: e.target.value ? Math.round(Number(e.target.value) * 100) : null })}
              className="w-full border border-border px-3 py-2 rounded text-sm bg-background"
            />
          </div>
          <div className="space-y-1">
            <label className="text-sm font-medium">Stock</label>
            <input
              type="number"
              min="0"
              placeholder="e.g. 10"
              value={form.stock || ""}
              onChange={(e) => setForm({ ...form, stock: Number(e.target.value) })}
              className="w-full border border-border px-3 py-2 rounded text-sm bg-background"
            />
          </div>
        </div>

        {/* Rating */}
        <div className="grid sm:grid-cols-2 gap-3">
          <div className="space-y-1">
            <label className="text-sm font-medium">Rating (0–5)</label>
            <input
              type="number"
              min="0"
              max="5"
              step="0.1"
              placeholder="e.g. 4.3"
              value={form.rating ?? ""}
              onChange={(e) => setForm({ ...form, rating: Number(e.target.value) })}
              className="w-full border border-border px-3 py-2 rounded text-sm bg-background"
            />
          </div>
          <div className="space-y-1">
            <label className="text-sm font-medium">Reviews count</label>
            <input
              type="number"
              min="0"
              placeholder="e.g. 2100"
              value={form.reviews_count || ""}
              onChange={(e) => setForm({ ...form, reviews_count: Number(e.target.value) })}
              className="w-full border border-border px-3 py-2 rounded text-sm bg-background"
            />
          </div>
        </div>

        {/* Details */}
        <div className="grid sm:grid-cols-2 gap-3">
          <input required placeholder="Slug" value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} className="border border-border px-3 py-2 rounded text-sm bg-background" />
          <input required placeholder="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="border border-border px-3 py-2 rounded text-sm bg-background" />
          <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="border border-border px-3 py-2 rounded text-sm bg-background sm:col-span-2">
            {categories.length === 0 && <option value="all">all</option>}
            {categories.map((c) => (
              <option key={c.slug} value={c.slug}>{c.label}</option>
            ))}
          </select>
        </div>

        <textarea placeholder="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="w-full border border-border px-3 py-2 rounded text-sm bg-background" rows={2} />
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={form.featured} onChange={(e) => setForm({ ...form, featured: e.target.checked })} /> Featured
        </label>
        <button disabled={loading || !form.image_url} className="inline-flex items-center gap-2 bg-foreground text-background px-4 py-2 text-sm rounded disabled:opacity-50">
          {editingId ? <Save className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
          {loading ? "Working..." : !form.image_url ? "Upload at least 1 image" : editingId ? "Save changes" : "Add product"}
        </button>

      </form>

      <div className="rounded-lg border border-border divide-y divide-border bg-card">
        {items.length === 0 && <p className="p-5 text-sm text-muted-foreground">No products yet.</p>}
        {items.length > 0 && (
          <p className="px-4 py-2 text-[11px] text-muted-foreground bg-muted/30">Drag rows by the handle to reorder products.</p>
        )}
        {items.map((p, i) => (
          <div
            key={p.id}
            draggable
            onDragStart={(e) => { setRowDragIdx(i); e.dataTransfer.effectAllowed = "move"; }}
            onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; if (rowOverIdx !== i) setRowOverIdx(i); }}
            onDragLeave={() => { if (rowOverIdx === i) setRowOverIdx(null); }}
            onDrop={(e) => { e.preventDefault(); if (rowDragIdx !== null) reorderRows(rowDragIdx, i); setRowDragIdx(null); setRowOverIdx(null); }}
            onDragEnd={() => { setRowDragIdx(null); setRowOverIdx(null); }}
            className={`flex items-center gap-3 p-4 transition-colors ${rowDragIdx === i ? "opacity-50" : ""} ${rowOverIdx === i && rowDragIdx !== i ? "bg-muted/50" : ""}`}
          >
            <GripVertical className="h-4 w-4 text-muted-foreground cursor-grab active:cursor-grabbing shrink-0" />
            <img src={p.image_url} alt="" className="h-14 w-14 object-cover rounded bg-muted" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate">{p.name}</p>
              <p className="text-xs text-muted-foreground">{p.category} · ₹{(p.price_cents / 100).toFixed(0)} · stock {p.stock}{p.images?.length ? ` · +${p.images.length} img` : ""}</p>
            </div>
            <button onClick={() => startEdit(p)} className="text-foreground/70 p-2 hover:bg-muted rounded" title="Edit">
              <Pencil className="h-4 w-4" />
            </button>
            <button onClick={() => remove(p.id)} className="text-destructive p-2 hover:bg-destructive/10 rounded" title="Delete">
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
